import { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';

import {
  Box,
  ButtonBase,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router';

import Checkbox from '@/components/Checkbox';
import { useAppSelector } from '@/hooks/redux-hooks';
import useExportSettings from '@/pages/DataHub/Export/ExportSettings/useExportSettings';
import { ExportSettingsContext } from '@/pages/DataHub/Export/ExportSettingsContext';

const ExportSettingsComponent = ({ showMetadata = true }: { showMetadata: boolean }) => {
  const navigate = useNavigate();
  const { updateExportSettings } = useExportSettings();
  const { exportSettings } = useContext(ExportSettingsContext);
  const [settings, setSettings] = useState(exportSettings);

  const { exportType, includeMetadata, from, to, period, periodNumber } = settings;
  const updateExportSetting = useCallback((key: keyof typeof exportSettings, value: unknown) => {
    setSettings(prev => {
      return { ...prev, [key]: value };
    });
  }, []);

  const {
    userConfig: {
      timezone: { label: timezoneLabel },
      dateFormat: { value: dateFormatValue }
    }
  } = useAppSelector(state => state.userConfig);
  const handleChangeRadio = (e: ChangeEvent<HTMLInputElement>) => {
    updateExportSetting('exportType', e.target.value);
  };

  const periodNumberError =
    (!periodNumber || periodNumber.length < 1 || periodNumber === '0' || periodNumber === '00') &&
    exportType === 'range';
  const fromError = (!from || from?.toString() === 'Invalid Date') && exportType === 'dates';
  const toError = to?.toString() === 'Invalid Date' && exportType === 'dates';

  useEffect(() => {
    if (
      fromError ||
      toError ||
      periodNumberError ||
      JSON.stringify(settings) === JSON.stringify(exportSettings)
    )
      return;
    updateExportSettings(settings);
  }, [exportType, includeMetadata, from, to, period, periodNumber]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontSize: '14px'
      }}
    >
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ mb: 1.5, fontSize: 14 }}>Date settings</Typography>

      <FormControl>
        <RadioGroup
          aria-labelledby='demo-radio-buttons-group-label'
          value={exportType}
          onChange={handleChangeRadio}
        >
          <FormControlLabel
            control={<Radio />}
            label='All'
            value='all'
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 50,
              marginBottom: periodNumberError ? 2 : 0
            }}
          >
            <FormControlLabel
              control={<Radio />}
              label='Last'
              value='range'
            />
            <TextField
              error={periodNumberError}
              helperText={periodNumberError ? 'Please enter a valid number' : ''}
              size='small'
              slotProps={{
                htmlInput: {
                  maxLength: 2,
                  inputMode: 'numeric'
                }
              }}
              sx={{
                ml: 2,
                width: 45,
                '& .MuiInputBase-input': {
                  p: '7px 14px'
                },
                '.MuiFormHelperText-root': {
                  position: 'absolute',
                  ml: 0,
                  bottom: '-22px',
                  fontSize: '12px',
                  width: 150
                }
              }}
              value={periodNumber}
              variant='filled'
              onChange={e => {
                updateExportSetting('periodNumber', e.target.value.replace(/\D/g, ''));
              }}
            />
            <Select
              MenuProps={{
                onClick: e => {
                  e.preventDefault();
                }
              }}
              size='small'
              sx={{
                ml: 2,
                '& .MuiInputBase-input': {
                  p: '5.5px 14px',
                  backgroundColor: '#272d48'
                },
                '& .MuiMenu-paper': {
                  top: '108px!important',
                  left: '141px!important'
                }
              }}
              value={period}
              variant='filled'
              onChange={e => {
                updateExportSetting('period', e.target.value);
              }}
            >
              <MenuItem
                sx={{ fontSize: '14px' }}
                value='d'
              >
                Days
              </MenuItem>
              <MenuItem
                sx={{ fontSize: '14px' }}
                value='m'
              >
                Months
              </MenuItem>
              <MenuItem
                sx={{ fontSize: '14px' }}
                value='y'
              >
                Years
              </MenuItem>
            </Select>
          </Box>
          <FormControlLabel
            control={<Radio />}
            label='From'
            value='dates'
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mt: 1,
              gap: 1,
              '.MuiFormControl-root': { flex: 1 }
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              localeText={{
                fieldMonthPlaceholder: ({ format }) => format
              }}
            >
              <DatePicker
                format={dateFormatValue}
                slotProps={{
                  openPickerIcon: { fontSize: 'small' },

                  textField: {
                    focused: false,
                    size: 'small',

                    error: fromError,
                    helperText: fromError ? 'Please select a valid start date' : '',
                    placeholder: dateFormatValue.toLocaleUpperCase(),
                    variant: 'filled',
                    sx: {
                      '.MuiPickersInputBase-root': {
                        fontSize: '14px',
                        width: '100%',
                        flex: 1
                      },
                      '.MuiPickersInputBase-sectionsContainer': {
                        py: 1
                      },
                      '.MuiPickersInputBase-root::before': {
                        display: 'none'
                      }
                    }
                  }
                }}
                value={from}
                onChange={newValue => {
                  updateExportSetting('from', newValue);
                }}
              />
              <DatePicker
                format={dateFormatValue}
                slotProps={{
                  openPickerIcon: { fontSize: 'small' },
                  textField: {
                    error: toError,
                    helperText: toError ? 'Please select a valid date' : '',
                    placeholder: dateFormatValue.toLocaleUpperCase(),
                    variant: 'filled',
                    focused: false,

                    sx: {
                      '.MuiPickersInputBase-root': {
                        fontSize: '14px',
                        flex: 1
                      },
                      '.MuiPickersInputBase-root::before': {
                        display: 'none'
                      },
                      '.MuiPickersInputBase-sectionsContainer': {
                        py: 1,
                        width: '100%'
                      }
                    }
                  }
                }}
                sx={{ flex: 1 }}
                value={to}
                onChange={newValue => {
                  updateExportSetting('to', newValue);
                }}
              />
            </LocalizationProvider>
          </Box>
        </RadioGroup>
      </FormControl>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline'
        }}
      >
        Timezone: {timezoneLabel}{' '}
        <ButtonBase
          sx={{
            ml: 2,
            color: 'primary.main'
          }}
          onClick={() => navigate('/profile')}
        >
          Change in Settings
        </ButtonBase>
      </Box>
      <Divider sx={{ my: 2 }} />
      {showMetadata && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeMetadata}
                onChange={() => {
                  updateExportSetting('includeMetadata', !includeMetadata);
                }}
              />
            }
            label='Include metadata'
            sx={{ m: 0 }}
          />
          <Divider sx={{ my: 2 }} />
        </>
      )}
    </Box>
  );
};

export default ExportSettingsComponent;
